import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "react-date-range";
import { FiCalendar } from "react-icons/fi";
import {
  combineLocalDateTime,
  formatDate,
  formatDateTime,
  formatTime,
  getAnchoredBoundPosition,
  parseLocalIsoDate,
  toLocalIsoDate,
  toLocalIsoTime,
} from "../../../utils";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../DateRangePicker/DateRangePicker.css";
import "./DateTimePicker.css";

function splitTime12(timeHm) {
  const [h24, mi] = String(timeHm || toLocalIsoTime()).split(":").map((n) => parseInt(n, 10) || 0);
  const ampm = h24 >= 12 ? "PM" : "AM";
  let hour = h24 % 12;
  if (hour === 0) hour = 12;
  return { hour, minute: Number.isNaN(mi) ? 0 : mi, ampm };
}

function joinTime12(hour, minute, ampm) {
  let h24 = Number(hour) % 12;
  if (ampm === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(Number(minute) || 0).padStart(2, "0")}`;
}

/**
 * Date and/or time picker in the browser's local timezone.
 * `showDate` / `showTime` control which parts render (default: both).
 */
export default function DateTimePicker({
  date,
  time,
  onChange,
  showDate = true,
  showTime = true,
  label,
  placeholder,
  minDate,
  isInvalid = false,
  error = "",
  className = "",
}) {
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const ignoreTriggerClickRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [draftDate, setDraftDate] = useState(date || toLocalIsoDate());
  const [draftTime, setDraftTime] = useState(time || toLocalIsoTime());

  const defaultLabel = showDate && showTime ? "Date and time" : showDate ? "Date" : "Time";
  const defaultPlaceholder =
    showDate && showTime ? "Select date and time" : showDate ? "Select date" : "Select time";

  const resetDraft = () => {
    setDraftDate(date || toLocalIsoDate());
    setDraftTime(time || toLocalIsoTime());
  };

  const closePicker = (discard) => {
    ignoreTriggerClickRef.current = true;
    if (discard) resetDraft();
    setOpen(false);
  };

  useEffect(() => {
    if (!open) resetDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePos = () => {
      const el = rootRef.current;
      const pop = popoverRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const popoverWidth = Math.min(window.innerWidth - 24, 360);
      const popoverHeight = pop?.offsetHeight || 480;
      const pos = getAnchoredBoundPosition(rect, popoverWidth, popoverHeight, null, 12, 6);
      setPopoverPos({ top: pos.top, left: pos.left });
    };
    updatePos();
    const frame = requestAnimationFrame(updatePos);
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, showDate, showTime]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      closePicker(true);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date, time]);

  const formatPickerValue = (dateVal, timeVal) => {
    if (showDate && showTime) {
      const dt = combineLocalDateTime(dateVal, timeVal);
      return dt ? formatDateTime(dt) : "";
    }
    if (showDate) return dateVal ? formatDate(parseLocalIsoDate(dateVal)) : "";
    if (showTime) {
      const dt = combineLocalDateTime(toLocalIsoDate(), timeVal);
      return dt ? formatTime(dt) : "";
    }
    return "";
  };

  const fieldLabel = label == null ? defaultLabel : label;
  const displayLabel = formatPickerValue(date, time) || placeholder || defaultPlaceholder;
  const draftLabel = formatPickerValue(draftDate, draftTime);
  const timeParts = splitTime12(draftTime);

  const handleApply = (e) => {
    e.preventDefault();
    e.stopPropagation();
    ignoreTriggerClickRef.current = true;
    onChange({ date: showDate ? draftDate : date, time: showTime ? draftTime : time });
    setOpen(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    ignoreTriggerClickRef.current = true;
    onChange({
      date: showDate ? "" : date,
      time: showTime ? "" : time,
    });
    setDraftDate("");
    setDraftTime("");
    setOpen(false);
  };

  const popover = open
    ? createPortal(
        <div
          ref={popoverRef}
          className="ns-daterange__popover ns-daterange__popover--portal ns-datetime__popover"
          style={{ top: popoverPos.top, left: popoverPos.left }}
          role="dialog"
          aria-label={fieldLabel}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {showDate ? (
            <div className="ns-daterange__picker-wrap ns-datetime__calendar">
              <Calendar
                date={parseLocalIsoDate(draftDate)}
                onChange={(d) => setDraftDate(toLocalIsoDate(d))}
                minDate={minDate}
                color="#4c5fd5"
              />
            </div>
          ) : null}
          {showTime ? (
            <div className="ns-datetime__time">
              <span className="ns-datetime__time-label">Time</span>
              <div className="ns-datetime__time-row">
                <select
                  className="form-select"
                  value={timeParts.hour}
                  aria-label="Hour"
                  onChange={(e) =>
                    setDraftTime(joinTime12(e.target.value, timeParts.minute, timeParts.ampm))
                  }
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <select
                  className="form-select"
                  value={timeParts.minute}
                  aria-label="Minute"
                  onChange={(e) =>
                    setDraftTime(joinTime12(timeParts.hour, e.target.value, timeParts.ampm))
                  }
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>
                      {String(i).padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <select
                  className="form-select"
                  value={timeParts.ampm}
                  aria-label="AM or PM"
                  onChange={(e) =>
                    setDraftTime(joinTime12(timeParts.hour, timeParts.minute, e.target.value))
                  }
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          ) : null}
          <div className="ns-daterange__footer">
            <span className="ns-daterange__footer-range">{draftLabel}</span>
            <div className="ns-daterange__footer-actions">
              <button type="button" className="btn_theme_secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className="btn_theme_primary" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`ns-daterange ns-datetime ${className}`.trim()} ref={rootRef}>
      {fieldLabel ? <label className="ns-daterange__label">{fieldLabel}</label> : null}
      <button
        type="button"
        className={`ns-daterange__trigger form-control${isInvalid ? " is-invalid" : ""}`}
        onClick={() => {
          if (ignoreTriggerClickRef.current) {
            ignoreTriggerClickRef.current = false;
            return;
          }
          if (open) {
            closePicker(true);
            return;
          }
          resetDraft();
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={displayLabel}
      >
        <FiCalendar className="ns-daterange__icon" aria-hidden />
        <span className="ns-daterange__trigger-value">{displayLabel}</span>
      </button>
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      {popover}
    </div>
  );
}
